export interface RoomMatrixRoomType {
  /** The number of combinations (rows) the room type is part of */
  count: number;
  id: Content.Components['RoomMatrixRoomDto']['roomTypeId'];
  name: Content.Components['RoomMatrixRoomDto']['name'];
}

/**
 * Extract the unique room types from the room matrix rows
 * @param rows - The room matrix rows, where each row is a room combination
 * @returns The room types in order of first appearance, with the number of combinations they are part of
 */
export const getRoomTypes = (
  rows?: Content.Components['RoomMatrixRowDto'][],
): RoomMatrixRoomType[] => {
  const roomTypes = new Map<number, RoomMatrixRoomType>();

  rows?.forEach(row => {
    // A combination can contain the same room type multiple times, but it should only be counted once
    const rowRoomTypeIds = new Set<number>();

    row.rooms.forEach(room => {
      if (rowRoomTypeIds.has(room.roomTypeId)) return;

      rowRoomTypeIds.add(room.roomTypeId);

      const roomType = roomTypes.get(room.roomTypeId);

      if (roomType) {
        roomType.count++;
      } else {
        roomTypes.set(room.roomTypeId, {
          count: 1,
          id: room.roomTypeId,
          name: room.name,
        });
      }
    });
  });

  return Array.from(roomTypes.values());
};

/**
 * Filter the room matrix rows to the combinations containing all of the room types
 * @param rows - The room matrix rows, where each row is a room combination
 * @param roomTypeIds - The ids of the room types to filter by, all rows are returned when empty
 * @returns The filtered room matrix rows
 */
export const filterRowsByRoomTypes = (
  rows: Content.Components['RoomMatrixRowDto'][],
  roomTypeIds: RoomMatrixRoomType['id'][],
): Content.Components['RoomMatrixRowDto'][] => {
  if (roomTypeIds.length === 0) return rows;

  return rows.filter(row =>
    roomTypeIds.every(roomTypeId =>
      row.rooms.some(room => room.roomTypeId === roomTypeId),
    ),
  );
};
